import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Button } from 'react-bootstrap';
import { AiOutlineClose } from "react-icons/ai";
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import PropTypes from 'prop-types';

const ModalContext = createContext();

export const useModal = () => useContext(ModalContext);

const iniSettings = {
    disableClose: false,
    centered: false,
    size: 'md',
    padContent: true
};

const ModalProvider = ({ children }) => {
    const { t } = useTranslation();
    const [ onLoadEvent, setOnLoadEvent ] = useState(() => () => null);
    const [ onCloseEvent, setOnCloseEvent ] = useState(() => () => null);
    const [ content, setContent ] = useState(<></>);
    const [ title, setTitle ] = useState('');
    const [ modalSettings, setModalSettings ] = useState(iniSettings);
    const [ toggleModal, setToggleModal ] = useState(false);
    const [ fader, setFader ] = useState(undefined);
    

    const resetModal = useCallback(() => {
        setContent(<></>);
        setTitle('');
        setModalSettings(iniSettings);
        setOnCloseEvent(() => () => {});
        onLoadEvent(() => () => {});
        setToggleModal(false);
        setFader(undefined);
    }, []);

    useEffect(() => {
        if(toggleModal) {
            if(typeof onLoadEvent === 'function') {
                onLoadEvent();
            }
            const fadeEffect = setTimeout(() => {
                setFader('show');
            }, 150);
            return () => {
                clearTimeout(fadeEffect);
                const removeFader = setTimeout(() => {
                    setToggleModal(false);
                    if (typeof onCloseEvent === 'function')
                        onCloseEvent();
                }, 150);
                resetModal();
            };
        }
    }, [toggleModal]);

    // Set transition timing
    const transition = '.35s ease-in-out';

    const showModal = () => setToggleModal(true);

    const hideModal = () => setToggleModal(false);

    const modalStatus = toggleModal? 'active' : 'inactive';

    return (
        <ModalContext.Provider value={{
            setContent,
            setTitle,
            setOnLoadEvent,
            setOnCloseEvent,
            showModal,
            hideModal,
            setModalSettings,
            modalStatus
        }}>
            {children}
            {toggleModal && (
                <ModalView
                    size={modalSettings?.size}
                    fader={fader}
                    centered={modalSettings?.centered}
                    disableClose={modalSettings?.disableClose}
                    onCloseEvent={onCloseEvent}
                    title={title}
                    padContent={modalSettings?.padContent}
                    transition={transition}
                >
                    {content}
                </ModalView>
            )}
        </ModalContext.Provider>
    );
};

ModalProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default ModalProvider;


export const ModalView = ({
    children,
    size,
    fader,
    centered,
    disableClose,
    onCloseEvent,
    title,
    padContent,
    footer,
    transition = 'all 0.3s ease-in-out'
}) => {
    return (
        <>
        <div className='modal-overlay'>
        <Box
            className={`modal modal-${size || 'md'} ${fader || ''} modal-blur`}
            tabIndex="-1"
            role="dialog"
            sx={{
                display: 'block',
                position: 'fixed',
                width:'90%',
                zIndex: 1501,
                boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                borderColor: 'var(--tblr-border-color)',
                transition,
                opacity: fader === 'show' ? 1 : 0
            }}
            aria-modal="true"
        >
            <Box
                className={`modal-dialog ${centered ? 'modal-dialog-centered' : ''}`}
                role="document"
                // onClick={e => e.stopPropagation()}
                style={{
                    alignItems: 'center',
                    transition,
                    transform: `translateY(${fader === 'show' ? '0' : '-30px'})`,
                }}
            >
                <Box className="modal-content">
                    {!disableClose && (
                    <Button
                        aria-label={t('Close')}
                        onClick={ () => {
                            if(typeof onCloseEvent === 'function')
                                onCloseEvent();
                        }}
                        variant='text'
                        style={{
                        display:'flex',    
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 'auto',
                        textDecoration: 'none',
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        zIndex: 2,
                        border: 'none',
                        boxShadow: 'none',
                        background:'transparent'
                        }}
                    ><AiOutlineClose size={20} />
                    </Button>
                    )}
                    {title && <Typography variant="h6" style={{ fontSize: '1rem', margin: '5px', textAlign: 'left' }}>{title}</Typography>}
                    <Box
                        className={padContent ? 'm-4' : ''}
                        style={{
                            maxHeight: 'calc(96vh - 130px)',
                            overflowY: 'auto' }
                        }>
                            {children}
                    </Box>
                    {footer && (
                        <Box className="modal-footer">
                            {footer}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
        <Box
            className={`modal-backdrop ${fader || ''} modal-blur`}
            style={{
            transition,
            zIndex: 1500,
            opacity: fader === 'show' ? .24 : 0,
            }}>    
        </Box>
        </div>
        </>
    );
};

ModalView.propTypes = {
    children: PropTypes.node.isRequired,
    footer: PropTypes.node,
    size: PropTypes.string,
    fader: PropTypes.string,
    centered: PropTypes.bool,
    disableClose: PropTypes.bool,
    onCloseEvent: PropTypes.func,
    title: PropTypes.string,
    padContent: PropTypes.bool,
    transition: PropTypes.string
};